import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Check,
	CloudUpload,
	Eye,
	Film,
	ImageIcon,
	Link2,
	Loader2,
	PenLine,
	Play,
	Search,
	Trash2,
	Video,
	X,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardHeaderSection,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import {
	apiRequest,
	type UploadedMediaAsset,
	useUnifiedPostCrud,
} from "@/components/dashboard/posts/use-unified-post-crud";
import { VideoThumbnail } from "@/components/dashboard/posts/VideoThumbnail";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/media")({
	component: MediaPage,
});

type MediaAsset = UploadedMediaAsset & {
	createdAt: string | number | Date;
	originalFilename: string | null;
	originalSourceUrl: string | null;
	sourceKind: "upload" | "url";
	status: string;
	storageKey: string;
	updatedAt: string | number | Date;
};

type MediaFilter = "all" | "image" | "video";

const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/avif";
const ACCEPTED_VIDEO_TYPES = "video/mp4,video/quicktime";
const ACCEPTED_MEDIA_TYPES = `${ACCEPTED_IMAGE_TYPES},${ACCEPTED_VIDEO_TYPES}`;

const mediaDateFormatter = new Intl.DateTimeFormat(undefined, {
	day: "numeric",
	month: "short",
	year: "numeric",
});

function formatBytes(bytes: number) {
	if (bytes >= 1024 * 1024 * 1024) {
		return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
	}
	if (bytes >= 1024 * 1024) {
		return `${Math.ceil(bytes / (1024 * 1024))} MB`;
	}
	return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
}

function formatDate(value: string | number | Date) {
	return mediaDateFormatter.format(new Date(value));
}

function getAssetName(asset: MediaAsset) {
	return (
		asset.originalFilename ||
		asset.originalSourceUrl ||
		`${asset.mediaType}-${asset.assetId.slice(0, 8)}`
	);
}

function getAssetSourceLabel(asset: MediaAsset) {
	return asset.sourceKind === "url" ? "Imported" : "Uploaded";
}

function MediaPage() {
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const imageInputRef = useRef<HTMLInputElement | null>(null);
	const videoInputRef = useRef<HTMLInputElement | null>(null);
	const [filter, setFilter] = useState<MediaFilter>("all");
	const [search, setSearch] = useState("");
	const [remoteUrl, setRemoteUrl] = useState("");
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
	const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);
	const [pendingDelete, setPendingDelete] = useState<MediaAsset | null>(null);
	const { workspace } = useDashboardContext();
	const queryClient = useQueryClient();
	const { uploadMediaMutation } = useUnifiedPostCrud();

	const deleteMutation = useMutation({
		mutationFn: async (assetId: string) => {
			return apiRequest<{ deleted: string }>(`/api/social/media/${assetId}`, {
				method: "DELETE",
			});
		},
		onSuccess: async (_payload, assetId) => {
			await queryClient.invalidateQueries({ queryKey: ["media-assets"] });
			toast.success("Media deleted");
			setPendingDelete(null);
			setSelectedAssetId((current) => (current === assetId ? null : current));
			setPreviewAsset((current) =>
				current?.assetId === assetId ? null : current,
			);
		},
		onError: (error) => {
			toast.error(
				error instanceof Error ? error.message : "Failed to delete media",
			);
		},
	});

	const mediaQuery = useQuery({
		enabled: Boolean(workspace?.id),
		queryKey: ["media-assets", workspace?.id],
		queryFn: async () => {
			const payload = await apiRequest<{ assets: MediaAsset[] }>(
				"/api/social/media",
			);
			return payload.assets ?? [];
		},
	});

	const assets = mediaQuery.data ?? [];
	const normalizedSearch = search.trim().toLowerCase();
	const mediaStats = useMemo(
		() => ({
			total: assets.length,
			images: assets.filter((asset) => asset.mediaType === "image").length,
			videos: assets.filter((asset) => asset.mediaType === "video").length,
			imported: assets.filter((asset) => asset.sourceKind === "url").length,
		}),
		[assets],
	);
	const filteredAssets = useMemo(
		() =>
			assets.filter((asset) => {
				if (filter !== "all" && asset.mediaType !== filter) {
					return false;
				}
				if (!normalizedSearch) {
					return true;
				}
				const searchableText = [
					getAssetName(asset),
					asset.originalSourceUrl,
					asset.mediaType,
					getAssetSourceLabel(asset),
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return searchableText.includes(normalizedSearch);
			}),
		[assets, filter, normalizedSearch],
	);
	const selectedAsset = useMemo(
		() => assets.find((asset) => asset.assetId === selectedAssetId) ?? null,
		[assets, selectedAssetId],
	);
	const hasAssets = assets.length > 0;

	async function uploadFiles(files: File[]) {
		if (files.length === 0) return;
		await Promise.all(
			files.map((file) => uploadMediaMutation.mutateAsync({ file })),
		);
		await queryClient.invalidateQueries({ queryKey: ["media-assets"] });
		toast.success(files.length === 1 ? "Media uploaded" : "Media uploaded");
	}

	function handleFiles(fileList: FileList | File[]) {
		const files = Array.from(fileList).filter(
			(file) =>
				file.type.startsWith("image/") || file.type.startsWith("video/"),
		);
		if (files.length === 0) {
			toast.error("Choose an image or video file");
			return;
		}
		void uploadFiles(files);
	}

	async function importRemoteUrl() {
		const url = remoteUrl.trim();
		if (!url) return;
		await uploadMediaMutation.mutateAsync({ sourceUrl: url });
		setRemoteUrl("");
		setImportDialogOpen(false);
		await queryClient.invalidateQueries({ queryKey: ["media-assets"] });
		toast.success("Media imported");
	}

	function toggleAssetSelection(assetId: string) {
		setSelectedAssetId((current) => (current === assetId ? null : assetId));
	}

	function clearMediaFilters() {
		setFilter("all");
		setSearch("");
	}

	return (
		<DashboardLayout>
			<DashboardHeaderSection contentClassName="gap-4 lg:flex-row lg:items-center lg:justify-between">
				<DashboardPageHeader
					title="Media Library"
					description="Search, preview, and reuse images or videos before adding them to posts."
				/>
				<div className="flex shrink-0 items-center gap-2">
					<button
						type="button"
						onClick={() => setImportDialogOpen(true)}
						className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/40 dark:bg-input/30 dark:hover:bg-input/50"
					>
						<Link2 className="mr-1.5 size-3.5" />
						Import from URL
					</button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
							>
								<CloudUpload className="mr-1.5 size-3.5" />
								Upload
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									imageInputRef.current?.click();
								}}
							>
								<ImageIcon className="mr-2 size-4" />
								Upload image
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									videoInputRef.current?.click();
								}}
							>
								<Video className="mr-2 size-4" />
								Upload video
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</DashboardHeaderSection>
			<DashboardContent>
				{hasAssets ? (
					<div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
						{[
							{ label: "Total assets", value: mediaStats.total },
							{ label: "Images", value: mediaStats.images },
							{ label: "Videos", value: mediaStats.videos },
							{ label: "Imported", value: mediaStats.imported },
						].map((stat) => (
							<div
								key={stat.label}
								className="rounded-md border border-border bg-muted/20 px-3 py-2"
							>
								<p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
									{stat.label}
								</p>
								<p className="mt-1 text-lg font-semibold tabular-nums text-foreground">
									{stat.value}
								</p>
							</div>
						))}
					</div>
				) : null}

				<input
					ref={fileInputRef}
					type="file"
					accept={ACCEPTED_MEDIA_TYPES}
					multiple
					className="hidden"
					onChange={(event) => {
						if (event.currentTarget.files) {
							handleFiles(event.currentTarget.files);
						}
						event.currentTarget.value = "";
					}}
				/>
				<input
					ref={imageInputRef}
					type="file"
					accept={ACCEPTED_IMAGE_TYPES}
					multiple
					className="hidden"
					onChange={(event) => {
						if (event.currentTarget.files) {
							handleFiles(event.currentTarget.files);
						}
						event.currentTarget.value = "";
					}}
				/>
				<input
					ref={videoInputRef}
					type="file"
					accept={ACCEPTED_VIDEO_TYPES}
					multiple
					className="hidden"
					onChange={(event) => {
						if (event.currentTarget.files) {
							handleFiles(event.currentTarget.files);
						}
						event.currentTarget.value = "";
					}}
				/>

				<button
					type="button"
					onClick={() => fileInputRef.current?.click()}
					onDragEnter={(event) => {
						event.preventDefault();
						setDragActive(true);
					}}
					onDragOver={(event) => {
						event.preventDefault();
						setDragActive(true);
					}}
					onDragLeave={(event) => {
						event.preventDefault();
						setDragActive(false);
					}}
					onDrop={(event) => {
						event.preventDefault();
						setDragActive(false);
						handleFiles(event.dataTransfer.files);
					}}
					className={cn(
						"mb-4 flex min-h-28 w-full items-center justify-center gap-3 rounded-md border border-dashed px-4 py-4 text-left transition-colors",
						dragActive
							? "border-foreground bg-muted text-foreground"
							: "border-border bg-muted/20 text-muted-foreground hover:bg-muted/40",
					)}
				>
					<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground">
						{uploadMediaMutation.isPending ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<CloudUpload className="size-4" />
						)}
					</span>
					<span className="min-w-0">
						<span className="block text-sm font-medium text-foreground">
							<span className="sm:hidden">Tap to upload media</span>
							<span className="hidden sm:inline">
								Drop media here or click to upload
							</span>
						</span>
						<span className="mt-1 block text-xs text-muted-foreground">
							JPG, PNG, MP4, and MOV files.
						</span>
					</span>
				</button>

				{hasAssets ? (
					<div className="mb-4 flex flex-col gap-3 rounded-md border border-border bg-background p-3 lg:flex-row lg:items-center lg:justify-between">
						<div className="relative min-w-0 flex-1">
							<Search className="-translate-y-1/2 pointer-events-none absolute left-3 top-1/2 size-4 text-muted-foreground" />
							<input
								type="search"
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search media by filename, URL, or type"
								className="h-9 w-full rounded-md border border-border bg-muted/20 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
							/>
							{search ? (
								<button
									type="button"
									onClick={() => setSearch("")}
									aria-label="Clear media search"
									className="-translate-y-1/2 absolute right-2 top-1/2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								>
									<X className="size-3.5" />
								</button>
							) : null}
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<div className="inline-flex rounded-md border border-border bg-muted/20 p-0.5 text-xs font-medium">
								{(
									[
										{ value: "all", label: "All", count: mediaStats.total },
										{
											value: "image",
											label: "Images",
											count: mediaStats.images,
										},
										{
											value: "video",
											label: "Videos",
											count: mediaStats.videos,
										},
									] as const
								).map(({ value, label, count }) => (
									<button
										key={value}
										type="button"
										onClick={() => setFilter(value)}
										className={cn(
											"inline-flex h-7 items-center gap-1.5 rounded-sm px-2.5 transition-colors",
											filter === value
												? "bg-primary text-primary-foreground"
												: "text-muted-foreground hover:text-foreground",
										)}
									>
										{label}
										<span className="tabular-nums opacity-70">{count}</span>
									</button>
								))}
							</div>
							<span className="text-xs text-muted-foreground">
								{filteredAssets.length} shown
							</span>
						</div>
					</div>
				) : null}

				{selectedAsset ? (
					<div className="mb-4 flex flex-col gap-3 rounded-md border border-foreground/20 bg-muted/30 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
						<div className="min-w-0">
							<p className="truncate text-sm font-medium text-foreground">
								Selected: {getAssetName(selectedAsset)}
							</p>
							<p className="mt-0.5 text-xs text-muted-foreground">
								{selectedAsset.mediaType === "video" ? "Video" : "Image"} ·{" "}
								{formatBytes(selectedAsset.fileSizeBytes)} ·{" "}
								{getAssetSourceLabel(selectedAsset)}
							</p>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={() => setPreviewAsset(selectedAsset)}
								className="inline-flex h-8 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
							>
								<Eye className="mr-1.5 size-3.5" />
								Preview
							</button>
							<button
								type="button"
								onClick={() => setSelectedAssetId(null)}
								className="inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
							>
								Clear
							</button>
							<Link
								to="/dashboard/compose"
								search={{ assetId: selectedAsset.assetId }}
								className="inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
							>
								<PenLine className="mr-1.5 size-3.5" />
								Use in post
							</Link>
						</div>
					</div>
				) : null}

				{mediaQuery.isLoading ? (
					<div className="py-16 text-center text-sm text-muted-foreground">
						Loading media...
					</div>
				) : !hasAssets ? null : filteredAssets.length === 0 ? (
					<div className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-16 text-center">
						<p className="text-sm font-medium text-foreground">
							No matching media
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							Try a different search term or media type.
						</p>
						<div className="mt-4 flex flex-wrap items-center justify-center gap-2">
							<button
								type="button"
								onClick={clearMediaFilters}
								className="inline-flex h-8 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
							>
								Clear filters
							</button>
						</div>
					</div>
				) : (
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
						{filteredAssets.map((asset) => {
							const isSelected = asset.assetId === selectedAssetId;
							return (
								<div
									key={asset.assetId}
									className={cn(
										"group relative flex flex-col overflow-hidden rounded-md border bg-card text-left transition-all hover:border-foreground/30 hover:shadow-sm",
										isSelected
											? "border-foreground shadow-sm"
											: "border-border",
									)}
								>
									<button
										type="button"
										onClick={() => toggleAssetSelection(asset.assetId)}
										aria-pressed={isSelected}
										className="relative aspect-square overflow-hidden bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
									>
										{asset.mediaType === "video" ? (
											<VideoThumbnail
												assetId={asset.assetId}
												className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
												videoUrl={asset.sourceUrl}
											/>
										) : (
											<img
												alt=""
												className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
												src={asset.sourceUrl}
											/>
										)}
										<span
											className={cn(
												"absolute left-2 top-2 inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] font-medium",
												isSelected
													? "bg-primary text-primary-foreground"
													: "bg-neutral-950/70 text-white",
											)}
										>
											{isSelected ? (
												<>
													<Check className="size-3" />
													Selected
												</>
											) : (
												getAssetSourceLabel(asset)
											)}
										</span>
										{asset.mediaType === "video" ? (
											<span className="absolute bottom-2 left-2 inline-flex h-6 items-center gap-1 rounded-md bg-neutral-950/70 px-1.5 text-[10px] font-medium text-white">
												<Play className="size-2.5 fill-white" />
												Video
											</span>
										) : null}
									</button>
									<div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
										<button
											type="button"
											onClick={() => setPreviewAsset(asset)}
											aria-label={`Preview ${getAssetName(asset)}`}
											className="inline-flex size-7 items-center justify-center rounded-md bg-neutral-950/70 text-white transition-colors hover:bg-neutral-950/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<Eye className="size-3.5" />
										</button>
										<Link
											to="/dashboard/compose"
											search={{ assetId: asset.assetId }}
											aria-label={`Use ${getAssetName(asset)} in a post`}
											className="inline-flex size-7 items-center justify-center rounded-md bg-neutral-950/70 text-white transition-colors hover:bg-neutral-950/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<PenLine className="size-3.5" />
										</Link>
										<button
											type="button"
											onClick={() => setPendingDelete(asset)}
											aria-label={`Delete ${getAssetName(asset)}`}
											className="inline-flex size-7 items-center justify-center rounded-md bg-neutral-950/70 text-white transition-colors hover:bg-neutral-950/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<Trash2 className="size-3.5" />
										</button>
									</div>
									<div className="min-w-0 px-2.5 py-2">
										<p className="truncate text-xs font-medium text-foreground">
											{getAssetName(asset)}
										</p>
										<p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
											{asset.mediaType === "video" ? (
												<Film className="size-2.5" />
											) : (
												<ImageIcon className="size-2.5" />
											)}
											<span>{formatBytes(asset.fileSizeBytes)}</span>
											<span aria-hidden>·</span>
											<span>{formatDate(asset.createdAt)}</span>
										</p>
									</div>
								</div>
							);
						})}
					</div>
				)}

				<Dialog
					open={importDialogOpen}
					onOpenChange={(open) => {
						if (uploadMediaMutation.isPending) return;
						setImportDialogOpen(open);
						if (!open) {
							setRemoteUrl("");
						}
					}}
				>
					<DialogContent className="max-w-md">
						<DialogHeader>
							<DialogTitle>Import from URL</DialogTitle>
							<DialogDescription>
								Bring hosted images or videos into the library.
							</DialogDescription>
						</DialogHeader>
						<input
							type="url"
							value={remoteUrl}
							onChange={(event) => setRemoteUrl(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									event.preventDefault();
									void importRemoteUrl();
								}
							}}
							placeholder="https://example.com/media.mp4"
							className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
						/>
						<DialogFooter>
							<button
								type="button"
								onClick={() => {
									setImportDialogOpen(false);
									setRemoteUrl("");
								}}
								disabled={uploadMediaMutation.isPending}
								className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
							>
								Cancel
							</button>
							<button
								type="button"
								disabled={uploadMediaMutation.isPending || !remoteUrl.trim()}
								onClick={() => void importRemoteUrl()}
								className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{uploadMediaMutation.isPending ? (
									<Loader2 className="size-3.5 animate-spin" />
								) : (
									"Import"
								)}
							</button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<Dialog
					open={previewAsset !== null}
					onOpenChange={(open) => {
						if (!open) {
							setPreviewAsset(null);
						}
					}}
				>
					<DialogContent className="max-w-4xl overflow-hidden p-0">
						{previewAsset ? (
							<>
								<DialogHeader className="border-b border-border px-5 py-4 pr-12">
									<DialogTitle className="truncate">
										{getAssetName(previewAsset)}
									</DialogTitle>
									<DialogDescription>
										{previewAsset.mediaType === "video" ? "Video" : "Image"} ·{" "}
										{formatBytes(previewAsset.fileSizeBytes)} ·{" "}
										{getAssetSourceLabel(previewAsset)} ·{" "}
										{formatDate(previewAsset.createdAt)}
									</DialogDescription>
								</DialogHeader>
								<div className="flex max-h-[70vh] min-h-80 items-center justify-center bg-neutral-950">
									{previewAsset.mediaType === "video" ? (
										<video
											autoPlay
											className="max-h-[70vh] w-full object-contain"
											controls
											loop
											muted
											playsInline
											src={previewAsset.sourceUrl}
										/>
									) : (
										<img
											alt={getAssetName(previewAsset)}
											className="max-h-[70vh] w-full object-contain"
											src={previewAsset.sourceUrl}
										/>
									)}
								</div>
								<DialogFooter className="border-t border-border px-5 py-4">
									<Link
										to="/dashboard/compose"
										search={{ assetId: previewAsset.assetId }}
										className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
									>
										<PenLine className="mr-1.5 size-3.5" />
										Use in post
									</Link>
								</DialogFooter>
							</>
						) : null}
					</DialogContent>
				</Dialog>

				<AlertDialog
					open={pendingDelete !== null}
					onOpenChange={(open) => {
						if (!open && !deleteMutation.isPending) {
							setPendingDelete(null);
						}
					}}
				>
					<AlertDialogContent className="max-w-sm">
						<AlertDialogHeader>
							<AlertDialogTitle>Delete this media?</AlertDialogTitle>
							<AlertDialogDescription>
								{pendingDelete
									? `"${getAssetName(pendingDelete)}" will be permanently removed. Posts that reference it will lose this media.`
									: ""}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={deleteMutation.isPending}>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								disabled={deleteMutation.isPending}
								onClick={(event) => {
									event.preventDefault();
									if (pendingDelete) {
										deleteMutation.mutate(pendingDelete.assetId);
									}
								}}
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							>
								{deleteMutation.isPending ? (
									<Loader2 className="size-3.5 animate-spin" />
								) : (
									"Delete"
								)}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</DashboardContent>
		</DashboardLayout>
	);
}
